import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';

const PAGE_SIZE = 30;

const ThreadList = () => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  const loadThreads = useCallback(async (nextCursor = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getThreads(PAGE_SIZE, nextCursor);
      if (nextCursor) {
        setThreads((prev) => [...prev, ...data.items]);
      } else {
        setThreads(data.items || []);
      }
      setHasMore(data.has_more || false);
      setCursor(data.next_cursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load threads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const handleLoadMore = () => {
    if (cursor && !loading) loadThreads(cursor);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl font-semibold text-gray-100 mb-4">Admin – Threads</h1>

      {error && (
        <p className="mb-4 text-red-400 text-sm">{error}</p>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-800 text-gray-300">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Last message</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {threads.map((t) => (
              <tr key={t.thread_id} className="bg-gray-800/50 hover:bg-gray-800">
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/threads/${t.thread_id}`)}
                    className="text-blue-400 hover:text-blue-300 text-left font-medium"
                  >
                    {t.title || 'Untitled'}
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-400">
                  {t.user_email || t.user_id || '—'}
                </td>
                <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                  {t.last_message_preview || '—'}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {formatDate(t.updated_at)}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/threads/${t.thread_id}`)}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && threads.length === 0 && (
        <p className="py-8 text-gray-400 text-center">Loading threads...</p>
      )}

      {!loading && threads.length === 0 && !error && (
        <p className="py-8 text-gray-400 text-center">No threads found.</p>
      )}

      {hasMore && (
        <div className="mt-4">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loading}
            className={
              'rounded-lg px-4 py-2 text-sm font-medium ' +
              (loading
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-700 text-gray-200 hover:bg-gray-600')
            }
          >
            {loading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ThreadList;
